/**
 * LinkChannelModal - "Esta pessoa tambem fala por outro canal".
 *
 * A equipe procura, por nome ou numero, o contato da MESMA pessoa em outro
 * canal (WhatsApp, Instagram, Facebook) e confirma o vinculo: os dois viram
 * UM lead (etapa, responsavel, tags e campos passam a ser os mesmos; as
 * conversas continuam cada uma no seu inbox). Mesma acao que o agente faz
 * pela tool `cadastrar_lead` ao receber o WhatsApp de um lead do Instagram.
 *
 * Irreversivel pela tela: o backend so aceita o admin da empresa (403 para
 * membro), e o modal so e aberto pelo drawer quando o usuario e admin.
 * Espelha a linguagem visual do LinkNumbersModal.
 */
import React, { useEffect, useState } from 'react';
import { X, Link2, Loader2, Search, AlertTriangle, Check, Instagram, Facebook, Globe, MessageCircle, User } from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';
import { formatPhoneNumber } from '../../utils/wa-personal/chat_utils';

const ICON = { instagram: Instagram, messenger: Facebook, widget: Globe, wa_personal: MessageCircle, wa_business: MessageCircle };
const COLOR = { instagram: '#c13584', messenger: '#1877f2', widget: '#00a884', wa_personal: '#25d366', wa_business: '#00a884' };

const rotuloDaChave = (c) => {
  const key = String((c && c.key) || '');
  if (key.startsWith('instagram:') || key.startsWith('messenger:') || key.startsWith('widget:')) return c.label || key.split(':')[0];
  const numero = formatPhoneNumber(key.replace(/\D/g, ''));
  return `${c.label || 'WhatsApp'} ${numero || key}`.trim();
};

const LinkChannelModal = ({
  isOpen,
  onClose,
  contactKey,
  contactName,
  colors,
  zIndex = 1300,
  onLinked,   // (data) => void — depois de vincular
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    setQuery('');
    setResults([]);
    setSelected(null);
    setError(null);
    setLinking(false);
    return undefined;
  }, [isOpen, contactKey]);

  // Busca com atraso curto: uma chamada por pausa na digitacao.
  useEffect(() => {
    if (!isOpen) return undefined;
    const termo = query.trim();
    if (termo.length < 2) { setResults([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      setSearching(true);
      assignmentService.linkCandidates(contactKey, termo)
        .then((res) => { if (alive) setResults(Array.isArray(res?.data) ? res.data : []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [isOpen, query, contactKey]);

  const handleLink = async () => {
    if (!selected || linking) return;
    const alvo = (selected.channels || [])[0];
    if (!alvo?.key) return;
    setLinking(true);
    setError(null);
    try {
      const res = await assignmentService.linkChannel(contactKey, alvo.key);
      onLinked?.(res?.data || null);
      onClose?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) setError('Apenas o administrador da empresa pode vincular canais.');
      else if (status === 409) setError('Um dos contatos não foi encontrado na empresa.');
      else setError(typeof detail === 'string' ? detail : 'Não foi possível vincular agora. Tente de novo.');
    } finally {
      setLinking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: colors.bgPrimary, borderRadius: 16, width: '100%', maxWidth: 460,
          overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`, flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link2 size={18} color={colors.accentPrimary} /> Vincular a outro canal
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: colors.textSecondary }}>
              {contactName || contactKey}
            </p>
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary, padding: 8, borderRadius: 8 }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto', flex: 1 }}>
          <span style={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: '18px' }}>
            Procure a mesma pessoa em outro canal pelo nome ou pelo número. Os dois
            contatos viram um lead só: etapa, responsável, tags e campos passam a
            ser os mesmos. As conversas continuam cada uma no seu inbox.
          </span>
          <div style={{ position: 'relative' }}>
            <Search size={14} color={colors.textTertiary} style={{ position: 'absolute', left: 10, top: 11 }} />
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
              placeholder="Nome ou número (ex.: Ana Paula, 34 99630)"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 32px', borderRadius: 8,
                border: `1px solid ${colors.borderColor}`, backgroundColor: colors.bgSecondary,
                color: colors.textPrimary, fontSize: 13, outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>

          {searching && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.textTertiary, fontSize: 12 }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Procurando...
            </div>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <span style={{ fontSize: 12, color: colors.textTertiary }}>Nenhum contato com esse nome ou número.</span>
          )}
          {results.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {results.map((c) => {
                const ativo = selected?.company_contact_id === c.company_contact_id;
                return (
                  <button
                    key={c.company_contact_id}
                    type="button"
                    onClick={() => setSelected(ativo ? null : c)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 10,
                      border: `1px solid ${ativo ? colors.accentPrimary : colors.borderColor}`,
                      backgroundColor: ativo ? `${colors.accentPrimary}14` : colors.bgSecondary,
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                    }}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, backgroundColor: colors.bgTertiary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {c.photo ? (
                        <img src={c.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : <User size={16} color={colors.textTertiary} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name || 'Sem nome'}
                      </span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(c.channels || []).map((ch) => {
                          const Icon = ICON[ch.channel] || MessageCircle;
                          const color = COLOR[ch.channel] || '#25d366';
                          return (
                            <span key={`${ch.channel}:${ch.key}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600, color, backgroundColor: `${color}14`, border: `1px solid ${color}55`, borderRadius: 999, padding: '1px 7px' }}>
                              <Icon size={11} /> {rotuloDaChave(ch)}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    {ativo && <Check size={16} color={colors.accentPrimary} />}
                  </button>
                );
              })}
            </div>
          )}

          {selected && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10, borderRadius: 10, backgroundColor: 'rgba(245, 158, 11, 0.10)', border: '1px solid rgba(245, 158, 11, 0.35)' }}>
              <AlertTriangle size={15} color="#d97706" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 12, color: colors.textPrimary, lineHeight: '17px' }}>
                Confirme que <strong>{selected.name || 'este contato'}</strong> é a mesma pessoa. Depois
                de unificar não dá para separar pela tela.
              </span>
            </div>
          )}
          {error && (
            <span style={{ fontSize: 12, color: '#dc2626' }}>{error}</span>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: `1px solid ${colors.borderColor}`, flexShrink: 0 }}>
          <button type="button" onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: `1px solid ${colors.borderColor}`, background: 'transparent', color: colors.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleLink}
            disabled={!selected || linking}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, border: 'none',
              backgroundColor: colors.accentPrimary, color: '#fff', fontSize: 13, fontWeight: 600,
              cursor: !selected || linking ? 'default' : 'pointer', opacity: !selected || linking ? 0.6 : 1, fontFamily: 'inherit',
            }}
          >
            {linking ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={14} />}
            Unificar em um lead
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkChannelModal;
